import { useState, useEffect, useCallback } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { StockChangeLogEntry } from '../../../lib/api';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import FIFOAllocationDrawer from './FIFOAllocationDrawer';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface ChangeHistoryViewProps {
  pageSize: number;
}

/**
 * Read-only stock change log view. Owns its own fetch + pagination state so
 * the parent (StockUploadTab) doesn't have to track history alongside the
 * stock-input grid. Re-fetches whenever the parent changes the shared
 * pageSize selector.
 */
export default function ChangeHistoryView({ pageSize }: ChangeHistoryViewProps) {
  const [items, setItems] = useState<StockChangeLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const { formatDate } = useFormatters();
  const [totalCount, setTotalCount] = useState(0);
  // FIFO drill-down target: clicked item_code + description. Null = closed.
  const [fifoTarget, setFifoTarget] = useState<{ partNumber: string; description: string } | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await callOffAPI.getChangeHistory({ page, page_size: pageSize });
      setItems(res.data.items || []);
      setTotalCount(res.data.total_count || 0);
    } catch {
      // Non-critical: keep last-known list on error.
    }
  }, [page, pageSize]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No change history.
            </div>
          ) : (
            <Table wrapperClassName="max-h-[calc(100vh-340px)]">
              <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Item Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Previous Qty</TableHead>
                  <TableHead>Current Qty</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Changed By</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {entry.stock_reference ? (
                        <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-medium text-foreground">
                          {entry.stock_reference}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        onClick={() => setFifoTarget({ partNumber: entry.item_code, description: entry.item_description })}
                        className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
                        title="View FIFO allocation trail for this part"
                      >
                        {entry.item_code}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs">{entry.item_description}</TableCell>
                    <TableCell>{entry.previous_qty ?? '-'}</TableCell>
                    <TableCell>{entry.current_qty}</TableCell>
                    <TableCell>{entry.change_type}</TableCell>
                    <TableCell>{entry.change_source}</TableCell>
                    <TableCell>{entry.changed_by_name}</TableCell>
                    <TableCell>{formatDate(entry.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {fifoTarget && (
        <FIFOAllocationDrawer
          open={!!fifoTarget}
          onOpenChange={(open) => { if (!open) setFifoTarget(null); }}
          partNumber={fifoTarget.partNumber}
          description={fifoTarget.description}
        />
      )}
    </>
  );
}
